import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Target,
  HelpCircle,
  Award,
  Calendar,
  Lightbulb,
  Copy,
  CheckCircle,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  TrendingUp
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { cn } from '@shared/utils/cn';

interface InterviewStrategyCoachProps {
  jobMatchId: string;
}

interface JobMatch {
  id: string;
  job_title: string;
  company_name: string;
}

interface InterviewStrategy {
  interview_talking_points: any[] | null;
  weakness_strategies: any[] | null;
  interviewer_questions: any[] | null;
  brag_stories_highlighted: any[] | null;
  day_plan_30_60_90: any | null;
  overall_strategy: any | null;
  full_interview_strategy: string | null;
}

export const InterviewStrategyCoach: React.FC<InterviewStrategyCoachProps> = ({ jobMatchId }) => {
  const { user } = useAuth();
  const [jobMatch, setJobMatch] = useState<JobMatch | null>(null);
  const [strategy, setStrategy] = useState<InterviewStrategy | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['talking-points'])
  );
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [jobMatchId]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      const { data: matchData, error: matchError } = await supabase
        .from('job_matches')
        .select('id, job_title, company_name')
        .eq('id', jobMatchId)
        .maybeSingle();

      if (matchError) throw matchError;

      if (!matchData) {
        throw new Error('Job match not found');
      }

      setJobMatch(matchData);

      const { data: analysisData, error: analysisError } = await supabase
        .from('job_analysis')
        .select('*')
        .eq('job_match_id', jobMatchId)
        .maybeSingle();

      if (analysisError && analysisError.code !== 'PGRST116') {
        console.warn('Error loading analysis:', analysisError);
      }

      if (analysisData) {
        setStrategy(analysisData);
      }

      if (user) {
        await supabase
          .from('match_history')
          .insert({
            user_id: user.id,
            job_match_id: jobMatchId,
            interaction_type: 'interview_strategy_viewed',
            metadata: { source: 'interview_strategy_coach' }
          });
      }
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error('Failed to load interview strategy data');
    } finally {
      setIsLoading(false);
    }
  };

  const generateStrategy = async () => {
    if (!user) {
      toast.error('You must be logged in to generate interview strategies');
      return;
    }

    try {
      setIsGenerating(true);
      toast.info('Generating personalized interview strategy...');

      const { data, error } = await supabase.functions.invoke('generate-interview-strategy', {
        body: {
          jobMatchId,
          userId: user.id,
        },
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || 'Failed to generate strategy');
      }

      toast.success('Interview strategy generated successfully!');
      await loadData();
    } catch (error: any) {
      console.error('Error generating strategy:', error);
      toast.error(error.message || 'Failed to generate interview strategy');
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (text: string, itemId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemId);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const formatFieldName = (key: string): string => {
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-gray-600">Loading interview strategy coach...</p>
      </div>
    );
  }

  if (!jobMatch) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Job Match Not Found</h3>
        <p className="text-gray-600">Unable to load job match data.</p>
      </div>
    );
  }

  const hasStrategy =
    strategy?.interview_talking_points ||
    strategy?.weakness_strategies ||
    strategy?.interviewer_questions;

  if (!hasStrategy) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Sparkles className="h-8 w-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Generate Your Personalized Interview Strategy
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Get a comprehensive interview strategy that connects your BRAG sheet achievements to this
            role's requirements, addresses your weaknesses strategically, and provides thoughtful
            questions based on your DREAM sheet values.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h4 className="font-semibold text-blue-900 mb-3">Your Strategy Will Include:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <MessageSquare className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">5-7 Key Talking Points</p>
                <p className="text-sm text-blue-700">
                  Connected to your specific BRAG achievements
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Target className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Strategic Weakness Responses</p>
                <p className="text-sm text-blue-700">Honest approaches with mitigation plans</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <HelpCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">5-7 Questions for Interviewer</p>
                <p className="text-sm text-blue-700">Based on your DREAM sheet values</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Award className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">BRAG Stories in STAR Format</p>
                <p className="text-sm text-blue-700">Ready-to-use achievement examples</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">30-60-90 Day Plan</p>
                <p className="text-sm text-blue-700">If applicable for senior roles</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <TrendingUp className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Overall Strategy</p>
                <p className="text-sm text-blue-700">Opening, core message, and closing</p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Button
            onClick={generateStrategy}
            disabled={isGenerating}
            size="lg"
            className="px-8"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Generating Strategy...
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5 mr-2" />
                Generate Interview Strategy
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  const sections = [
    {
      id: 'talking-points',
      title: 'Key Talking Points',
      icon: <MessageSquare className="h-5 w-5" />,
      color: 'bg-blue-500',
      data: strategy.interview_talking_points || [],
      description: 'Strategic points connecting your strengths to role requirements',
    },
    {
      id: 'weakness-strategies',
      title: 'Strategic Weakness Responses',
      icon: <Target className="h-5 w-5" />,
      color: 'bg-orange-500',
      data: strategy.weakness_strategies || [],
      description: 'Honest approaches with clear mitigation strategies',
    },
    {
      id: 'interviewer-questions',
      title: 'Questions for Interviewer',
      icon: <HelpCircle className="h-5 w-5" />,
      color: 'bg-green-500',
      data: strategy.interviewer_questions || [],
      description: 'Thoughtful questions aligned with your DREAM values',
    },
    {
      id: 'brag-stories',
      title: 'BRAG Stories to Highlight',
      icon: <Award className="h-5 w-5" />,
      color: 'bg-yellow-500',
      data: strategy.brag_stories_highlighted || [],
      description: 'Specific achievements formatted in STAR method',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-border/40 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              Executive Interview Strategy
            </h2>
            <p className="text-gray-600">
              {jobMatch.job_title} at {jobMatch.company_name}
            </p>
          </div>
          <Button onClick={generateStrategy} disabled={isGenerating} variant="outline" size="sm">
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Regenerating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Regenerate
              </>
            )}
          </Button>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Lightbulb className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-1">
                Your Personalized Strategy is Ready
              </h4>
              <p className="text-sm text-gray-700">
                This strategy is built from your actual BRAG achievements, DREAM aspirations, and
                tailored specifically for this role. Review each section and practice your responses.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {sections.map((section) => {
            const isExpanded = expandedSections.has(section.id);
            const hasItems = section.data.length > 0;

            return (
              <div
                key={section.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={cn('p-2 rounded-lg text-white', section.color)}>
                      {section.icon}
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900">{section.title}</h3>
                      <p className="text-sm text-gray-600">
                        {section.data.length} {section.data.length === 1 ? 'item' : 'items'} •{' '}
                        {section.description}
                      </p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>

                {isExpanded && (
                  <div className="p-4 bg-white">
                    {hasItems ? (
                      <div className="space-y-4">
                        {section.data.map((item, index) => {
                          const itemId = `${section.id}-${index}`;
                          const itemText = JSON.stringify(item, null, 2);

                          return (
                            <div
                              key={index}
                              className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group"
                            >
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-white border border-gray-300 rounded-full text-xs font-medium text-gray-700">
                                  {index + 1}
                                </span>
                                <button
                                  onClick={() => copyToClipboard(itemText, itemId)}
                                  className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Copy to clipboard"
                                >
                                  {copiedItem === itemId ? (
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <Copy className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                                  )}
                                </button>
                              </div>
                              <div className="pl-9 space-y-2">
                                {Object.entries(item).map(([key, value]) => (
                                  <div key={key}>
                                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                                      {formatFieldName(key)}
                                    </p>
                                    <p className="text-sm text-gray-800">
                                      {typeof value === 'string' ? value : JSON.stringify(value)}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No items in this section
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {strategy.day_plan_30_60_90 && (
        <div className="bg-white rounded-xl border border-border/40 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-100 rounded-lg">
              <Calendar className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">30-60-90 Day Plan</h3>
              <p className="text-sm text-gray-600">Strategic roadmap for your first 90 days</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {strategy.day_plan_30_60_90.first_30_days && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-3">First 30 Days - Learn</h4>
                <ul className="space-y-2">
                  {strategy.day_plan_30_60_90.first_30_days.map((item: string, idx: number) => (
                    <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                      <span className="text-blue-600 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {strategy.day_plan_30_60_90.days_31_60 && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-3">Days 31-60 - Build</h4>
                <ul className="space-y-2">
                  {strategy.day_plan_30_60_90.days_31_60.map((item: string, idx: number) => (
                    <li key={idx} className="text-sm text-green-800 flex items-start gap-2">
                      <span className="text-green-600 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {strategy.day_plan_30_60_90.days_61_90 && (
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-orange-900 mb-3">Days 61-90 - Execute</h4>
                <ul className="space-y-2">
                  {strategy.day_plan_30_60_90.days_61_90.map((item: string, idx: number) => (
                    <li key={idx} className="text-sm text-orange-800 flex items-start gap-2">
                      <span className="text-orange-600 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {strategy.day_plan_30_60_90.success_metrics && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Success Metrics</h4>
              <p className="text-sm text-gray-700">{strategy.day_plan_30_60_90.success_metrics}</p>
            </div>
          )}
        </div>
      )}

      {strategy.overall_strategy && (
        <div className="bg-white rounded-xl border border-border/40 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Overall Interview Strategy</h3>
              <p className="text-sm text-gray-600">Your approach from opening to close</p>
            </div>
          </div>

          <div className="space-y-4">
            {strategy.overall_strategy.opening_approach && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-2">Opening Approach</h4>
                <p className="text-sm text-purple-800">{strategy.overall_strategy.opening_approach}</p>
              </div>
            )}

            {strategy.overall_strategy.core_message && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Core Message</h4>
                <p className="text-sm text-blue-800">{strategy.overall_strategy.core_message}</p>
              </div>
            )}

            {strategy.overall_strategy.differentiators && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2">Your Differentiators</h4>
                <p className="text-sm text-green-800">{strategy.overall_strategy.differentiators}</p>
              </div>
            )}

            {strategy.overall_strategy.closing_strong && (
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-orange-900 mb-2">Closing Strong</h4>
                <p className="text-sm text-orange-800">{strategy.overall_strategy.closing_strong}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-center gap-4">
        <Button onClick={() => window.print()} variant="outline" className="flex items-center gap-2">
          <Copy className="h-4 w-4" />
          Print Strategy
        </Button>
      </div>
    </div>
  );
};

export default InterviewStrategyCoach;
